/**
 * Quais traduções o build realmente publica — e como escolher entre elas.
 *
 * ── Por que existe ──────────────────────────────────────────────────────────
 *
 * `setTranslations` monta uma lista de candidatos (idioma do navegador → idioma
 * da loja → default) e tenta um por um, caindo para o seguinte quando o
 * `translateService.use()` falha. A intenção está certa; o que não funciona é
 * confiar na FALHA para descobrir que um idioma não existe.
 *
 * O motivo é o SPA fallback do CloudFront: `assets/i18n/es-419.json` não existe,
 * mas a resposta não é 404 — é **HTTP 200 com o `index.html`**. O `HttpClient`
 * baixa 34 KB de HTML, tenta `JSON.parse`, e só então falha, com
 * `SyntaxError: Unrecognized token '<'` embrulhado num `HttpErrorResponse` de
 * status 200. O fallback até acontece, mas depois de uma ida à rede inútil e de
 * um evento de erro que vai parar no Sentry e na telemetria própria como se
 * fosse defeito. Medido em produção (Sentry PEDIRDELIVERY-3), com navegadores
 * em `es-419` — espanhol da América Latina, que o build não publica.
 *
 * A cura é não pedir o que não existe. Esta lista é a fonte da verdade, e o
 * teste `idiomas-disponiveis.spec.ts` a confere contra `src/assets/i18n/` para
 * que acrescentar um arquivo sem acrescentar aqui (ou o contrário) falhe no CI
 * em vez de virar 404 mascarado de 200 na casa do usuário.
 */

/** Arquivos existentes em `src/assets/i18n/` (sem a extensão), em minúsculas. */
export const IDIOMAS_DISPONIVEIS: ReadonlyArray<string> = [
  'en',
  'en-us',
  'es',
  'pt',
  'pt-br',
  'pt-pt',
];

/**
 * Resolve um candidato para um idioma que EXISTE, ou `null`.
 *
 * Duas tentativas, nesta ordem:
 *  1. o código completo (`pt-br`);
 *  2. só a língua (`es-419` → `es`), que é o que o usuário de fato entende —
 *     e é melhor do que cair no português por não reconhecer a variante.
 */
export function resolverIdioma(candidato: string | null | undefined): string | null {
  if (!candidato) return null;

  // `navigator.language` vem com hífen (`pt-BR`), mas há sistemas que devolvem
  // underscore (`pt_BR`); normalizar aqui evita um falso "não existe".
  const normalizado = String(candidato).toLowerCase().replace(/_/g, '-').trim();
  if (!normalizado) return null;

  if (IDIOMAS_DISPONIVEIS.indexOf(normalizado) !== -1) return normalizado;

  const base = normalizado.split('-')[0];
  if (base && IDIOMAS_DISPONIVEIS.indexOf(base) !== -1) return base;

  return null;
}

/**
 * Filtra a lista de candidatos, na ordem, mantendo só os que existem e sem
 * repetir — `pt-br` do navegador e `ptBR` da loja resolvem para o mesmo arquivo,
 * e tentar duas vezes o mesmo idioma só duplicaria requisição.
 */
export function idiomasParaTentar(candidatos: Array<string | null | undefined>): string[] {
  const saida: string[] = [];
  for (const candidato of candidatos || []) {
    const idioma = resolverIdioma(candidato);
    if (idioma && saida.indexOf(idioma) === -1) saida.push(idioma);
  }
  return saida;
}
